import useApiCall, {ApiCallResponseData, makeApiCall} from "../hooks/CancellableApiCall";
import {ConversationMessageResponse, ConversationParticipantResponse, ConversationSummary} from "../data/ConversationResponse";
import {
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputBase,
    List,
    ListItemButton,
    MenuItem,
    Select,
    Snackbar,
    Alert,
    Stack,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, {PropsWithChildren, useCallback, useEffect, useMemo, useState} from "react";
import {LoadingContent} from "../components/LoadingContent";
import {useNavigate, useParams} from "react-router-dom";
import {formatTimestamp, generateProfilePath, parseIntOrNull} from "../utils";
import {TextEditor} from "../components/editor/TextEditor";
import {EditorContent} from "../components/editor/EditorContent";
import {RankChip} from "../components/RankChip";
import AddIcon from '@mui/icons-material/Add';
import {UserResponse} from "../data/UserResponse";
import {RankResponse} from "../data/RankResponse";
import {DivisionRoles} from "../data/DivisionRoles";
import {UserSelector} from "../components/UserSelector";
import {BlockUi} from "../components/BlockUi";
import useUser from "../hooks/useUser";
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import MailIcon from '@mui/icons-material/Mail';
import {ConfirmationDialog} from "../components/ConfirmationDialog";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {MiniProfileTarget} from "../components/MiniProfileTarget";
import {PoliceUser} from "../components/PoliceUser";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import {AlertType} from "../data/AlertType";
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import MessageIcon from '@mui/icons-material/Message';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import MarkunreadIcon from '@mui/icons-material/Markunread';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import CreateIcon from '@mui/icons-material/Create';
import ReplyIcon from '@mui/icons-material/Reply';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// --- Utility ---

function formatRelativeDate(timestamp: string): string {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString(window.navigator.language, {month: "short", day: "numeric"})
}

// --- Dialogs ---

interface InboxDialogProps extends PropsWithChildren {
    title: string
    buttonText: string
    open: boolean
    onClose: () => void
    onButtonClicked: () => void
}

const InboxDialog = (props: InboxDialogProps) => {
    return (
        <Dialog open={props.open} onClose={props.onClose}>
            <DialogTitle>{props.title}</DialogTitle>
            <DialogContent dividers>
                {props.children}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => props.onButtonClicked()}>{props.buttonText}</Button>
            </DialogActions>
        </Dialog>
    )
}

// --- Conversation List Item (Outlook-style) ---

interface ConversationListItemProps {
    conversation: ConversationSummary
    isSelected: boolean
    onSelect: () => void
}

const ConversationListItem = (props: ConversationListItemProps) => {
    const theme = useTheme()
    const {conversation, isSelected} = props
    const isUnread = !conversation.read
    const lastUser = conversation.lastPoster ?? conversation.creator
    const dateStr = conversation.lastMessageDate ?? conversation.createdAt

    const selectedBg = theme.palette.mode === "light" ? theme.palette.primary.light + "22" : theme.palette.primary.dark + "33"
    const hoverBg = theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[800]

    return (
        <ListItemButton
            selected={isSelected}
            onClick={props.onSelect}
            sx={{
                px: 1.5,
                py: 1,
                borderLeft: isUnread ? `3px solid ${theme.palette.primary.main}` : "3px solid transparent",
                backgroundColor: isSelected ? selectedBg : "transparent",
                "&:hover": {backgroundColor: isSelected ? selectedBg : hoverBg},
                "&.Mui-selected": {backgroundColor: selectedBg},
                "&.Mui-selected:hover": {backgroundColor: selectedBg},
            }}
        >
            <Stack direction="row" spacing={1.25} sx={{width: "100%", overflow: "hidden"}}>
                <Avatar
                    src={`/api/user/${lastUser.communityId}/avatar`}
                    sx={{width: 36, height: 36, mt: 0.25}}
                />
                <Stack sx={{flex: 1, overflow: "hidden", minWidth: 0}}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                            variant="body2"
                            noWrap
                            sx={{fontWeight: isUnread ? 700 : 400, flex: 1}}
                        >
                            {lastUser.nick}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ml: 1, whiteSpace: "nowrap"}}>
                            {formatRelativeDate(dateStr)}
                        </Typography>
                    </Stack>
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{fontWeight: isUnread ? 600 : 400}}
                    >
                        {conversation.title}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" color="text.secondary" noWrap sx={{flex: 1}}>
                            {conversation.replyCount} messages · {conversation.participantCount} participants
                        </Typography>
                        {conversation.locked && <LockOutlinedIcon sx={{fontSize: 14, color: "text.secondary"}}/>}
                    </Stack>
                </Stack>
            </Stack>
        </ListItemButton>
    )
}

// --- Conversation Message ---

interface ConversationMessageProps {
    message: ConversationMessageResponse
}

const ConversationMessage = (props: ConversationMessageProps) => {
    const user = props.message.user
    const navigate = useNavigate()
    const onClick = () => {
        navigate(generateProfilePath(user.communityId))
    }

    return (
        <Box sx={{py: 1.5}}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <MiniProfileTarget communityId={user.communityId}>
                    <Avatar
                        src={`/api/user/${user.communityId}/avatar`}
                        sx={{width: 32, height: 32, cursor: "pointer"}}
                        onClick={onClick}
                    />
                </MiniProfileTarget>
                <Stack sx={{flex: 1, minWidth: 0}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <RankChip rank={user.nick} color={user.rank.color} onClick={onClick}/>
                        <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(props.message.date)}
                        </Typography>
                    </Stack>
                    <Box sx={{mt: 0.75}}>
                        <EditorContent content={props.message.content}/>
                    </Box>
                </Stack>
            </Stack>
        </Box>
    )
}

// --- Reading Pane (Messages + Header) ---

interface ReadingPaneProps {
    conversation: ConversationSummary
    refreshConversations: () => void
}

const ReadingPane = (props: ReadingPaneProps) => {
    const loadedMessages = useApiCall<ConversationMessageResponse[]>({
        url: `/api/conversation/${props.conversation.id}/messages`,
    })

    useEffect(() => {
        if (loadedMessages.isLoading || loadedMessages.isError || !loadedMessages.data) return
        if (!props.conversation.read) {
            props.refreshConversations()
        }
    }, [loadedMessages.data, loadedMessages.isLoading, loadedMessages.isError, props])

    const user = useUser()
    const [apiCall, setApiCall] = useState<ApiCallResponseData>()
    const [dialogVisible, setDialogVisible] = useState(false)
    const [leaveApiCall, setLeaveApiCall] = useState<ApiCallResponseData>()
    const [replyExpanded, setReplyExpanded] = useState(false)
    const navigate = useNavigate()

    const isNewConversation = !loadedMessages.isLoading && loadedMessages.data && loadedMessages.data.length === 0

    useEffect(() => {
        if (isNewConversation) {
            setReplyExpanded(true)
        }
    }, [isNewConversation])

    const cancelOldLeaveApiCall = useCallback(() => {
        leaveApiCall?.cancel()
    }, [leaveApiCall])

    const cancelOldSubmitApiCall = useCallback(() => {
        apiCall?.cancel()
    }, [apiCall])

    useEffect(() => {
        return cancelOldSubmitApiCall
    }, [cancelOldSubmitApiCall])

    useEffect(() => {
        return cancelOldLeaveApiCall
    }, [cancelOldLeaveApiCall])

    const onSubmitPressed = (html: string) => {
        const conversationId = props.conversation?.id
        if (!conversationId) return
        cancelOldSubmitApiCall()
        setApiCall(makeApiCall({
            url: `/api/conversation/${conversationId}/messages`,
            method: "POST",
            body: {content: html},
            onLoadedCallback: () => {
                loadedMessages.refresh()
                props.refreshConversations()
            },
            onError: () => {}
        }))
    }

    const onLeaveConfirmed = () => {
        const conversationId = props.conversation?.id
        if (!conversationId) return
        cancelOldLeaveApiCall()
        setLeaveApiCall(makeApiCall({
            url: `/api/conversation/${props.conversation.id}/participant/${user.user?.communityId}`,
            method: "DELETE",
            onLoadedCallback: () => {
                navigate("/inbox")
                props.refreshConversations()
            },
            onError: () => {}
        }))
    }

    const lockConversation = () => {
        makeApiCall({
            url: `/api/conversation/${props.conversation.id}/lock/`,
            method: "PATCH",
            body: {id: props.conversation.id, locked: true},
            onLoadedCallback: () => props.refreshConversations(),
            onError: () => {}
        })
    }

    const unlockConversation = () => {
        makeApiCall({
            url: `/api/conversation/${props.conversation.id}/lock/`,
            method: "PATCH",
            body: {id: props.conversation.id, locked: false},
            onLoadedCallback: () => props.refreshConversations(),
            onError: () => {}
        })
    }

    const canLock = user.hasPermissions("conversation:lock") || props.conversation?.creator?.communityId === user.user?.communityId

    return (
        <Box sx={{display: "flex", flexDirection: "column", height: "100%", overflow: "hidden"}}>
            <ConfirmationDialog
                title="Leave Conversation"
                description="Are you sure you want to leave the conversation?"
                open={dialogVisible}
                onClose={(confirmed) => {
                    setDialogVisible(false)
                    if (confirmed) onLeaveConfirmed()
                }}
            />

            {/* Header */}
            <Box sx={{px: {xs: 1.5, md: 2.5}, py: 1.5, borderBottom: 1, borderColor: "divider"}}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{fontWeight: 600, flex: 1, fontSize: {xs: "1rem", md: "1.25rem"}}} noWrap>
                        {props.conversation.title}
                    </Typography>
                    {canLock && (
                        props.conversation.locked
                            ? <Tooltip title="Unlock Conversation">
                                <IconButton size="small" onClick={unlockConversation}><LockOpenOutlinedIcon fontSize="small"/></IconButton>
                              </Tooltip>
                            : <Tooltip title="Lock Conversation">
                                <IconButton size="small" onClick={lockConversation}><LockOutlinedIcon fontSize="small"/></IconButton>
                              </Tooltip>
                    )}
                    <Tooltip title="Leave Conversation">
                        <IconButton size="small" onClick={() => setDialogVisible(true)}>
                            <ExitToAppIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 0.5, flexWrap: "wrap", gap: 0.5}}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <PersonOutlineIcon sx={{fontSize: 16, color: "text.secondary"}}/>
                        <PoliceUser linkProps={{variant: "caption"}} user={props.conversation.creator}/>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccessTimeIcon sx={{fontSize: 16, color: "text.secondary"}}/>
                        <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(props.conversation.createdAt)}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <MessageIcon sx={{fontSize: 16, color: "text.secondary"}}/>
                        <Typography variant="caption" color="text.secondary">
                            {props.conversation.replyCount} replies
                        </Typography>
                    </Stack>
                    {props.conversation.locked && (
                        <Chip label="Locked" size="small" color="warning" variant="outlined" sx={{height: 20, fontSize: "0.7rem"}}/>
                    )}
                </Stack>
            </Box>

            {/* Messages */}
            <Box sx={{flex: isNewConversation ? 0 : 1, overflow: "auto", px: {xs: 1.5, md: 2.5}, py: 0.5, minHeight: isNewConversation ? 0 : undefined}}>
                <LoadingContent isLoading={loadedMessages.isLoading}>
                    <Stack divider={<Divider/>}>
                        {loadedMessages.data && loadedMessages.data.map(message => (
                            <ConversationMessage key={message.id} message={message}/>
                        ))}
                    </Stack>
                </LoadingContent>
            </Box>

            {/* Reply Editor - Collapsible */}
            {!props.conversation.locked && (
                <Box sx={{borderTop: isNewConversation ? 0 : 1, borderColor: "divider", flex: isNewConversation ? 1 : undefined, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0}}>
                    {!isNewConversation && (
                        <Box
                            onClick={() => setReplyExpanded(!replyExpanded)}
                            sx={{
                                px: {xs: 1.5, md: 2.5},
                                py: 0.75,
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                "&:hover": {bgcolor: "action.hover"},
                                userSelect: "none",
                            }}
                        >
                            <ReplyIcon sx={{fontSize: 18, color: "text.secondary", mr: 1}}/>
                            <Typography variant="body2" color="text.secondary" sx={{flex: 1}}>
                                {replyExpanded ? "Hide reply" : "Reply to conversation..."}
                            </Typography>
                            <ExpandLessIcon sx={{
                                fontSize: 20,
                                color: "text.secondary",
                                transform: replyExpanded ? "rotate(0deg)" : "rotate(180deg)",
                                transition: "transform 0.2s ease",
                            }}/>
                        </Box>
                    )}
                    {replyExpanded && (
                        <Box sx={{
                            px: isNewConversation ? {xs: 1.5, md: 4} : {xs: 1.5, md: 2.5},
                            pb: 1.5,
                            pt: isNewConversation ? 2 : 0,
                            flex: isNewConversation ? 1 : undefined,
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 0,
                            "& .editor-scroller": {maxHeight: "30vh"},
                        }}>
                            {isNewConversation && (
                                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                    Compose your first message:
                                </Typography>
                            )}
                            <TextEditor onSubmitPressed={(html) => {
                                onSubmitPressed(html)
                                if (!isNewConversation) setReplyExpanded(false)
                            }} submitButtonText="Send"/>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    )
}

interface ParticipantsPanelProps {
    conversation: ConversationSummary
    ranks: RankResponse[]
    roles: DivisionRoles[]
}

const ParticipantsPanel = (props: ParticipantsPanelProps) => {
    const ownUserData = useUser()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [dialogRankOpen, setRankDialogOpen] = useState(false)
    const [dialogRoleOpen, setRoleDialogOpen] = useState(false)
    const [user, setUser] = useState<UserResponse | null>(null)
    const [apiCall, setApiCall] = useState<ApiCallResponseData>()
    const [removeApiCall, setRemoveApiCall] = useState<ApiCallResponseData>()
    const [userToRemove, setUserToRemove] = useState<ConversationParticipantResponse>()
    const [removeDescription, setRemoveDescription] = useState("")
    const conversationId = props.conversation.id
    const [rankId, setRankId] = useState(-1)
    const [roleId, setRoleId] = useState(-1)
    const navigate = useNavigate()

    const loadedParticipants = useApiCall<ConversationParticipantResponse[]>({
        url: `/api/conversation/${props.conversation.id}/participant`,
    })
    const isOwnConversation = props.conversation?.creator?.communityId === ownUserData.user?.communityId

    useEffect(() => { return () => { apiCall?.cancel() } }, [apiCall])
    useEffect(() => { return () => { removeApiCall?.cancel() } }, [removeApiCall])

    const [showAlert, setShowAlert] = useState(false)
    const [alertType, setAlertType] = useState<AlertType>("success")
    const [alertText, setAlertText] = useState("")
    const setAlert = useCallback((type: AlertType, text: string) => {
        setAlertType(type)
        setAlertText(text)
        setShowAlert(true)
    }, [])

    return (
        <Box sx={{
            width: {xs: "100%", lg: 240},
            borderLeft: {lg: 1},
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: "100%"
        }}>
            <ConfirmationDialog
                title="Remove from Conversation"
                description={removeDescription}
                open={userToRemove !== undefined}
                onClose={(confirmed) => {
                    const communityId = userToRemove?.communityId
                    setUserToRemove(undefined)
                    if (!communityId || !confirmed) return
                    setRemoveApiCall(makeApiCall({
                        url: `/api/conversation/${conversationId}/participant/${communityId}`,
                        method: "DELETE",
                        onLoadedCallback: () => {
                            loadedParticipants.refresh()
                            setRemoveApiCall(undefined)
                            setAlert("success", "User removed")
                        },
                        onError: () => {
                            setRemoveApiCall(undefined)
                            setAlert("error", "Failed to remove user")
                        }
                    }))
                }}
            />
            <InboxDialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setUser(null) }}
                onButtonClicked={() => {
                    const communityId = user?.communityId
                    if (!communityId) return
                    setApiCall(makeApiCall({
                        url: `/api/conversation/${conversationId}/participant/${communityId}`,
                        method: "PUT",
                        onLoadedCallback: () => {
                            setUser(null)
                            setDialogOpen(false)
                            loadedParticipants.refresh()
                            setApiCall(undefined)
                            setAlert("success", "User added")
                        },
                        onError: () => {
                            setApiCall(undefined)
                            setAlert("error", "Failed to add user")
                        }
                    }))
                }}
                buttonText="Invite" title="Invite to Conversation"
            >
                <BlockUi open={apiCall != null}>
                    <Typography variant="subtitle2" sx={{mb: 1}}>
                        Who do you want to add?
                    </Typography>
                    <UserSelector user={user} extendedUserInformation={true} onUserSelected={setUser}/>
                </BlockUi>
            </InboxDialog>
            <InboxDialog
                open={dialogRankOpen}
                onClose={() => { setRankDialogOpen(false); setRankId(-1) }}
                onButtonClicked={() => {
                    setApiCall(makeApiCall({
                        url: `/api/conversation/${conversationId}/participant/rank/${rankId}`,
                        method: "PUT",
                        body: {id: props.conversation.id, rankId: rankId},
                        onLoadedCallback: () => {
                            setRankId(-1)
                            setRankDialogOpen(false)
                            loadedParticipants.refresh()
                            setApiCall(undefined)
                            setAlert("success", "Rank added")
                        },
                        onError: () => {
                            setApiCall(undefined)
                            setAlert("error", "Failed to add rank")
                        }
                    }))
                }}
                buttonText="Invite Rank" title="Invite by Rank"
            >
                <BlockUi open={apiCall !== undefined}>
                    <Typography variant="subtitle2" sx={{mb: 1}}>Select a rank:</Typography>
                    <FormControl fullWidth>
                        <Select value={rankId} onChange={(e) => setRankId(e.target.value as number)}>
                            {props.ranks?.map((r) => <MenuItem key={r.rankId} value={r.rankId}>{r.displayName}</MenuItem>)}
                        </Select>
                    </FormControl>
                </BlockUi>
            </InboxDialog>
            <InboxDialog
                open={dialogRoleOpen}
                onClose={() => { setRoleDialogOpen(false); setRoleId(-1) }}
                onButtonClicked={() => {
                    setApiCall(makeApiCall({
                        url: `/api/conversation/${conversationId}/participant/role/${roleId}`,
                        method: "PUT",
                        onLoadedCallback: () => {
                            setRoleId(-1)
                            setRoleDialogOpen(false)
                            loadedParticipants.refresh()
                            setApiCall(undefined)
                            setAlert("success", "Role added")
                        },
                        onError: () => {
                            setApiCall(undefined)
                            setAlert("error", "Failed to add role")
                        }
                    }))
                }}
                buttonText="Invite Role" title="Invite by Role"
            >
                <BlockUi open={apiCall !== undefined}>
                    <Typography variant="subtitle2" sx={{mb: 1}}>Select a role:</Typography>
                    <FormControl fullWidth>
                        <Select value={roleId} onChange={(e) => setRoleId(e.target.value as number)}>
                            {props.roles?.map((r) => <MenuItem key={r.roleId} value={r.roleId}>{r.displayName}</MenuItem>)}
                        </Select>
                    </FormControl>
                </BlockUi>
            </InboxDialog>

            <Box sx={{px: 2, py: 1.5, borderBottom: 1, borderColor: "divider"}}>
                <Stack direction="row" alignItems="center">
                    <PeopleOutlineIcon sx={{fontSize: 18, mr: 0.5, color: "text.secondary"}}/>
                    <Typography variant="subtitle2" sx={{fontWeight: 600, flex: 1}}>
                        Participants ({loadedParticipants.data?.length ?? 0})
                    </Typography>
                    <IconButton size="small" onClick={() => setDialogOpen(true)}>
                        <AddIcon fontSize="small"/>
                    </IconButton>
                    {ownUserData.user?.permissions.find(p => p.includes("mass-add")) !== undefined && (
                        <IconButton size="small" onClick={() => setRoleDialogOpen(true)}>
                            <GroupAddIcon fontSize="small"/>
                        </IconButton>
                    )}
                    {ownUserData.hasPermissions("conversation:add-ranks") && (
                        <IconButton size="small" onClick={() => setRankDialogOpen(true)}>
                            <AddModeratorIcon fontSize="small"/>
                        </IconButton>
                    )}
                </Stack>
            </Box>

            <Box sx={{flex: 1, overflow: "auto", py: 1}}>
                <LoadingContent isLoading={loadedParticipants.isLoading}>
                    {loadedParticipants.data && loadedParticipants.data.map(participant => (
                        <Stack
                            key={participant.communityId}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{px: 2, py: 0.5, "&:hover": {bgcolor: "action.hover"}}}
                        >
                            <Avatar
                                src={`/api/user/${participant.communityId}/avatar`}
                                sx={{width: 28, height: 28, cursor: "pointer"}}
                                onClick={() => navigate(generateProfilePath(participant.communityId))}
                            />
                            <MiniProfileTarget communityId={participant.communityId}>
                                <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{flex: 1, cursor: "pointer", color: participant.rank?.color ? `#${participant.rank.color}` : "inherit"}}
                                    onClick={() => navigate(generateProfilePath(participant.communityId))}
                                >
                                    {participant.nick}
                                </Typography>
                            </MiniProfileTarget>
                            {(ownUserData.user?.rank?.immunity ?? 0) >= 5 && (
                                <Tooltip title={participant.read ? "Has read" : "Not yet read"}>
                                    <DoneAllIcon sx={{fontSize: 14, color: participant.read ? "primary.main" : "text.disabled"}}/>
                                </Tooltip>
                            )}
                            {((participant.communityId !== ownUserData.user?.communityId && isOwnConversation) || ownUserData.hasPermissions("conversation:lock")) && (
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                        setUserToRemove(participant)
                                        setRemoveDescription(`Remove ${participant.nick} from this conversation?`)
                                    }}
                                    sx={{opacity: 0.6, "&:hover": {opacity: 1}}}
                                >
                                    <RemoveCircleOutlineIcon sx={{fontSize: 16}}/>
                                </IconButton>
                            )}
                        </Stack>
                    ))}
                </LoadingContent>
            </Box>
            <Snackbar open={showAlert} autoHideDuration={4000} onClose={() => setShowAlert(false)}>
                <Alert severity={alertType} onClose={() => setShowAlert(false)} sx={{width: "100%"}} variant="filled">
                    {alertText}
                </Alert>
            </Snackbar>
        </Box>
    )
}

// --- Main Inbox Page (Outlook-style 3-pane layout, single-pane on mobile) ---

type FilterMode = "all" | "unread"

export const InboxPage = () => {
    const params = useParams()
    const navigate = useNavigate()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("md"))
    const isCompact = useMediaQuery(theme.breakpoints.down("lg"))
    const [filterMode, setFilterMode] = useState<FilterMode>("all")
    const [searchQuery, setSearchQuery] = useState("")
    const [newDialogOpen, setNewDialogOpen] = useState(false)
    const [newTitle, setNewTitle] = useState("")
    const [createApiCall, setCreateApiCall] = useState<ApiCallResponseData>()
    const [showParticipants, setShowParticipants] = useState(true)
    const [mobileParticipantsOpen, setMobileParticipantsOpen] = useState(false)

    const paramId = parseIntOrNull(params.id ?? "")

    const conversationSummaries = useApiCall<ConversationSummary[]>({
        url: "/api/conversation"
    })

    const loadRanks = useApiCall<RankResponse[]>({
        initialUrl: `/api/rank/conversation-rank-list`
    })

    const loadRoles = useApiCall<DivisionRoles[]>({
        initialUrl: `/api/conversation/mass-inbox-roles-list`
    })

    useEffect(() => {
        if (!isMobile && !paramId && conversationSummaries.data && !conversationSummaries.isLoading && conversationSummaries.data.length > 0) {
            navigate(`/inbox/${conversationSummaries.data[0].id}`)
        }
    }, [conversationSummaries.data, conversationSummaries.isLoading, navigate, paramId, isMobile])

    useEffect(() => { return () => { createApiCall?.cancel() } }, [createApiCall])

    const currentConversation = conversationSummaries.isLoading
        ? null
        : conversationSummaries.data?.find(a => a.id === paramId) ?? null

    const filteredConversations = useMemo(() => {
        if (!conversationSummaries.data) return null
        let result = conversationSummaries.data
        if (filterMode === "unread") {
            result = result.filter(c => !c.read)
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase()
            result = result.filter(c =>
                c.title.toLowerCase().includes(q) ||
                c.creator.nick.toLowerCase().includes(q) ||
                c.creator.rpName.toLowerCase().includes(q) ||
                (c.lastPoster?.nick.toLowerCase().includes(q) ?? false) ||
                (c.lastPoster?.rpName.toLowerCase().includes(q) ?? false)
            )
        }
        return result
    }, [conversationSummaries.data, filterMode, searchQuery])

    const unreadCount = useMemo(() => {
        return conversationSummaries.data?.filter(c => !c.read).length ?? 0
    }, [conversationSummaries.data])

    const selectConversation = useCallback((id: number) => {
        navigate(`/inbox/${id}`)
    }, [navigate])

    const markAllRead = () => {
        makeApiCall({
            url: "/api/conversation/read-all",
            method: "POST",
            onLoadedCallback: () => conversationSummaries.refresh(),
            onError: () => {}
        })
    }

    const createConversation = () => {
        if (newTitle.trim() === "") return
        setNewDialogOpen(false)
        setCreateApiCall(makeApiCall<ConversationSummary>({
            url: `/api/conversation`,
            method: "POST",
            body: {title: newTitle},
            onLoadedCallback: (newConversation) => {
                setNewTitle("")
                selectConversation(newConversation.id)
                conversationSummaries.refresh()
            },
            onError: () => {}
        }))
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 140px)",
                overflow: "hidden",
                borderRadius: 1,
                bgcolor: "background.paper",
            }}
        >
            {/* Create Conversation Dialog */}
            <InboxDialog
                open={newDialogOpen}
                onClose={() => { setNewDialogOpen(false); setNewTitle("") }}
                onButtonClicked={createConversation}
                buttonText="Create"
                title="New Conversation"
            >
                <TextField
                    autoFocus
                    margin="dense"
                    label="Subject"
                    fullWidth
                    variant="standard"
                    value={newTitle}
                    onChange={e => setNewTitle(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault()
                            e.stopPropagation()
                            createConversation()
                        }
                    }}
                />
                <Typography variant="caption" color="text.secondary" sx={{mt: 1}}>
                    Add participants after creating the conversation.
                </Typography>
            </InboxDialog>

            {/* Top Toolbar */}
            <Toolbar
                variant="dense"
                sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    minHeight: 44,
                    px: 1.5,
                    gap: 0.5
                }}
            >
                {isMobile && currentConversation && (
                    <IconButton size="small" onClick={() => navigate("/inbox")} sx={{mr: 0.5}}>
                        <ArrowBackIcon fontSize="small"/>
                    </IconButton>
                )}
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<CreateIcon/>}
                    onClick={() => setNewDialogOpen(true)}
                    sx={{textTransform: "none", mr: 1}}
                >
                    New
                </Button>
                <Tooltip title="Mark all as read">
                    <IconButton size="small" onClick={markAllRead} disabled={unreadCount === 0}>
                        <DoneAllIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
                {!isCompact && (
                    <Tooltip title={showParticipants ? "Hide participants" : "Show participants"}>
                        <IconButton size="small" onClick={() => setShowParticipants(!showParticipants)}>
                            <PeopleOutlineIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                )}
                {isCompact && currentConversation && (
                    <Tooltip title="Participants">
                        <IconButton size="small" onClick={() => setMobileParticipantsOpen(true)}>
                            <PeopleOutlineIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                )}
                <Box sx={{flex: 1}}/>
                {unreadCount > 0 && (
                    <Chip
                        icon={<MarkunreadIcon sx={{fontSize: 16}}/>}
                        label={`${unreadCount} unread`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                )}
            </Toolbar>

            {/* Main Content Area */}
            <Box sx={{display: "flex", flex: 1, overflow: "hidden"}}>
                {/* Left Pane: Conversation List (hidden on mobile when a conversation is selected) */}
                <Box sx={{
                    width: {xs: "100%", md: 320},
                    minWidth: {md: 260},
                    borderRight: {md: 1},
                    borderColor: "divider",
                    display: isMobile && currentConversation ? "none" : "flex",
                    flexDirection: "column",
                    overflow: "hidden"
                }}>
                    {/* Filter tabs + Search */}
                    <Box sx={{px: 1.5, pt: 1, pb: 0.75}}>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Chip
                                icon={<InboxIcon sx={{fontSize: 14}}/>}
                                label="All"
                                size="small"
                                variant={filterMode === "all" ? "filled" : "outlined"}
                                onClick={() => setFilterMode("all")}
                                color={filterMode === "all" ? "primary" : "default"}
                                sx={{height: 26}}
                            />
                            <Chip
                                icon={<MailIcon sx={{fontSize: 14}}/>}
                                label="Unread"
                                size="small"
                                variant={filterMode === "unread" ? "filled" : "outlined"}
                                onClick={() => setFilterMode("unread")}
                                color={filterMode === "unread" ? "primary" : "default"}
                                sx={{height: 26}}
                            />
                            <Box sx={{flex: 1}}/>
                            <InputBase
                                placeholder="Search..."
                                size="small"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                startAdornment={<SearchIcon sx={{fontSize: 16, color: "text.disabled", mr: 0.5}}/>}
                                sx={{
                                    fontSize: "0.8rem",
                                    bgcolor: "action.hover",
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.25,
                                    maxWidth: {xs: "100%", sm: 140},
                                    minWidth: {xs: 0},
                                    flex: {xs: 1, sm: "none"},
                                }}
                            />
                        </Stack>
                    </Box>

                    {/* Conversation List */}
                    <Box sx={{flex: 1, overflow: "auto"}}>
                        <LoadingContent isLoading={conversationSummaries.isLoading}>
                            <List disablePadding>
                                {filteredConversations && filteredConversations.length > 0
                                    ? filteredConversations.map(conv => (
                                        <React.Fragment key={conv.id}>
                                            <ConversationListItem
                                                conversation={conv}
                                                isSelected={currentConversation?.id === conv.id}
                                                onSelect={() => selectConversation(conv.id)}
                                            />
                                            <Divider component="li"/>
                                        </React.Fragment>
                                    ))
                                    : (
                                        <Box sx={{p: 3, textAlign: "center"}}>
                                            <Typography variant="body2" color="text.secondary">
                                                {filterMode === "unread" ? "No unread conversations" : "No conversations yet"}
                                            </Typography>
                                        </Box>
                                    )
                                }
                            </List>
                        </LoadingContent>
                    </Box>
                </Box>

                {/* Center Pane: Reading Pane (full-width on mobile when conversation selected) */}
                <Box sx={{
                    flex: 1,
                    display: isMobile && !currentConversation ? "none" : "flex",
                    flexDirection: "column",
                    overflow: "hidden"
                }}>
                    {currentConversation ? (
                        <ReadingPane
                            key={currentConversation.id}
                            conversation={currentConversation}
                            refreshConversations={() => conversationSummaries.refresh()}
                        />
                    ) : (
                        <Box sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            gap: 1
                        }}>
                            <InboxIcon sx={{fontSize: 64, color: "text.disabled"}}/>
                            <Typography variant="h6" color="text.secondary">
                                Select a conversation
                            </Typography>
                            <Typography variant="body2" color="text.disabled">
                                Choose a conversation from the left to read messages
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Right Pane: Participants (hidden on compact/mobile screens) */}
                {!isCompact && showParticipants && currentConversation && loadRanks.data && loadRoles.data && (
                    <ParticipantsPanel
                        conversation={currentConversation}
                        ranks={loadRanks.data}
                        roles={loadRoles.data}
                    />
                )}
            </Box>

            {/* Participants Dialog for compact/mobile screens */}
            {isCompact && currentConversation && loadRanks.data && loadRoles.data && (
                <Dialog
                    open={mobileParticipantsOpen}
                    onClose={() => setMobileParticipantsOpen(false)}
                    fullWidth
                    maxWidth="xs"
                    PaperProps={{sx: {height: "70vh", maxHeight: "70vh"}}}
                >
                    <ParticipantsPanel
                        conversation={currentConversation}
                        ranks={loadRanks.data}
                        roles={loadRoles.data}
                    />
                </Dialog>
            )}
        </Box>
    )
}
